import React, { useCallback, useLayoutEffect, useRef } from "react";
import { ScrollPickerColumn, ScrollPickerColumnExternalProps } from "./ScrollPickerColumn";

export type PickerValue = Record<string, string>;

// Descriptor-only component: its props are read by the parent ScrollPicker which renders
// a ScrollPickerColumn in its place. It is never mounted itself.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function ColumnDescriptor(_: ScrollPickerColumnExternalProps): null {
  return null;
}
ColumnDescriptor.displayName = "ScrollPicker.Column";

interface ScrollPickerRootProps {
  value: PickerValue;
  onChange: (value: PickerValue) => void;
  height: number;
  itemHeight: number;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /** Background of the fixed center-row pill (defaults to the light theme's lavender). */
  highlightColor?: string;
}

const GRADIENT_MASK =
  "linear-gradient(to bottom, transparent 0%, #000 20%, #000 80%, transparent 100%)";

function ScrollPickerRoot({
  value,
  onChange,
  height,
  itemHeight,
  children,
  className,
  style,
  highlightColor,
}: ScrollPickerRootProps) {
  const rootRef = useRef<HTMLDivElement>(null);

  // Ionic sheet modals install touchstart/mousedown listeners on their wrapper.
  // Letting picker gesture starts bubble to that wrapper makes the sheet drag
  // alongside the numbers and prevents the picker from owning the interaction.
  // A native listener is required here because Ionic's gesture listener is also
  // native; React's delegated event handler runs too late to stop it reliably.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const stopGestureStartPropagation = (event: Event) => event.stopPropagation();

    root.addEventListener("touchstart", stopGestureStartPropagation, { passive: true });
    root.addEventListener("mousedown", stopGestureStartPropagation);

    return () => {
      root.removeEventListener("touchstart", stopGestureStartPropagation);
      root.removeEventListener("mousedown", stopGestureStartPropagation);
    };
  }, []);

  // Refs keep the handler stable (never reinstalled in column effects) while always
  // reading the latest value/onChange.
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  useLayoutEffect(() => {
    valueRef.current = value;
    onChangeRef.current = onChange;
  });

  const handleColumnChange = useCallback((name: string, colValue: string) => {
    onChangeRef.current({ ...valueRef.current, [name]: colValue });
  }, []);

  const columns = React.Children.map(children, (child) => {
    if (!React.isValidElement(child) || child.type !== ColumnDescriptor) {
      return child;
    }
    const { name, options, renderItem } = child.props as ScrollPickerColumnExternalProps;
    return (
      <ScrollPickerColumn
        key={name}
        name={name}
        options={options}
        renderItem={renderItem}
        columnValue={value[name] ?? options[0] ?? ""}
        onColumnChange={handleColumnChange}
        height={height}
        itemHeight={itemHeight}
        highlightColor={highlightColor}
      />
    );
  });

  return (
    <div
      ref={rootRef}
      className={className}
      style={{
        display: "flex",
        height,
        overflow: "hidden",
        WebkitMaskImage: GRADIENT_MASK,
        maskImage: GRADIENT_MASK,
        ...style,
      }}
    >
      {columns}
    </div>
  );
}

export const ScrollPicker = Object.assign(ScrollPickerRoot, {
  Column: ColumnDescriptor,
});
